import React, { useEffect, useMemo, useState } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useCookies } from 'next-client-cookies';
import toast from 'react-hot-toast';
import { Cart, LineItem } from 'shared/types/cart';
import { DistributionChannel } from 'shared/types/cart/DistributionChannel';
import { mutate } from 'swr';
import ColoredTag from 'components/commercetools-ui/molecules/product-tile/components/tag';
import { useAddToCartOverlay } from 'context/add-to-cart-overlay';
import { P2R_SHIPPING_METHOD_KEY, P2R_VALUES } from 'helpers/constants/cart';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import useRefreshImages from 'helpers/hooks/useRefreshImages';
import { getAttributeLabel } from 'helpers/utils/attributes';
import { cleanString } from 'helpers/utils/util';
import { usePageLoader } from 'providers/loader';
import { sdk } from 'sdk';
import { useCart, useProjectSettings } from 'frontastic';
import { miniCartsKey } from 'frontastic/hooks/useCart/helper';
import MediaClipElement from './MediaClipElement';
import OrderDetailsPriceRow from './OrderDetailsPriceRow';
import OrderItemsThumbnailImage from './OrderItemsThumbnailImage';
import { ReasonCode } from '../../account/types/types';
import { getChildLineItems, getImageMetadataIdsFromCustomFields, mergeLineItems } from '../../cart/helpers';
import { OrderItemsSectionProps } from '../types';

type BaseOrderItem = {
  image: string;
  title: string;
  subTitle: string;
  attributes?: string;
  oldPrice?: string | null;
};

export type PickupItem = BaseOrderItem & {
  price?: string;
  currentPrice?: string;
  qty?: number;
  total?: string;
  imageLoading?: boolean;
  custom?: {
    type?: {
      typeId?: string;
      id?: string;
    };
    fields?: {
      mediaClipProjectId?: string;
      jurisdictionIds?: string;
      imageMetadataIds?: unknown;
    };
  };
  isMediaClipProduct?: boolean;
  reorder?: {
    quantity: number;
    distributionChannel?: DistributionChannel;
    productSku?: string;
  };
  _url?: string;
  name?: string;
  lineItemId: string;
  isSale: boolean;
};

export type ShipLine = {
  label?: string;
  oldPrice?: string | null;
  price?: string;
  qty?: number;
  total?: string;
};

export type ShipPrices = {
  price?: string;
  currentPrice?: string;
  total?: string;
};

const getMergedLineItemsTotals = (mergedLineItems: LineItem, locale: string): ShipPrices => {
  return {
    price: CurrencyHelpers.formatForCurrency(
      {
        centAmount: mergedLineItems.originalPrice?.centAmount,
        currencyCode: mergedLineItems.originalPrice?.currencyCode,
      },
      locale,
    ),
    currentPrice: CurrencyHelpers.formatForCurrency(
      {
        centAmount: mergedLineItems.price?.centAmount,
        currencyCode: mergedLineItems.price?.currencyCode,
      },
      locale,
    ),
    total: CurrencyHelpers.formatForCurrency(
      {
        centAmount: mergedLineItems.totalPrice?.centAmount,
        currencyCode: mergedLineItems.totalPrice?.currencyCode,
      },
      locale,
    ),
  };
};

const getFormattedTotals = (lineItems: any[], locale: string): ShipPrices => {
  if (!lineItems?.length) {
    return {
      price: '',
      currentPrice: '',
      total: '',
    };
  }

  const currencyCode = lineItems[0]?.totalPrice?.currencyCode;

  const formatCent = (amount: number) =>
    amount ? CurrencyHelpers.formatForCurrency({ centAmount: amount, currencyCode }, locale) : '';

  const totals = lineItems.reduce(
    (acc, item) => {
      const price = item?.price?.centAmount ?? 0;

      const discountedPricePerCount = item?.discountedPricePerCount || [];
      const discountedPrice =
        discountedPricePerCount.length > 0 ? discountedPricePerCount[0]?.discountedPrice : item?.discountedPrice;

      const currentPrice = discountedPrice?.value?.centAmount ?? price;

      const total = item?.totalPrice?.centAmount ?? 0;

      acc.price += price;
      acc.currentPrice += currentPrice;
      acc.total += total;

      return acc;
    },
    { price: 0, currentPrice: 0, total: 0 },
  );

  return {
    price: formatCent(totals.price),
    currentPrice: formatCent(totals.currentPrice),
    total: formatCent(totals.total),
  };
};

const normalizeItem = (item: any, locale: string): PickupItem => {
  const cent = (v?: number) =>
    v ? CurrencyHelpers.formatForCurrency({ centAmount: v, currencyCode: item?.totalPrice?.currencyCode }, locale) : '';

  const isMediaClip = Boolean(
    item.variant?.attributes?.mediaclip_product_id_universal !== undefined &&
    item.variant?.attributes?.mediaclip_product_id_universal?.toLowerCase() !== 'none' &&
    item.variant?.attributes?.mediaclip_module_universal,
  );
  const image =
    item?.imageMetadata?.urlThumb ||
    item?.imageMetadata?.url ||
    item?.imageMetadata?.projectThumbnailUrl ||
    item?.variant?.images?.[0] ||
    '/placeholder.png';

  const isDiscountApplied = item.discountedPrice && item.originalPrice?.centAmount !== item.price?.centAmount;

  return {
    image,
    imageLoading: false,
    title: item?.variant?.name || item?.name || '',
    subTitle: item?.variant?.attributes?.short_description_universal || '',
    attributes: isMediaClip
      ? (item?.variant?.attributes?.display_name_universal ?? item?.selectedVariantOptions)
      : getAttributeLabel(item?.variant?.attributes, 'size_pp')?.toString() ||
      getAttributeLabel(item?.variant?.attributes, 'size_wa')?.toString() ||
      item?.variant?.attributes?.product_code_prefix ||
      '',
    oldPrice: isDiscountApplied ? cent(item?.originalPrice?.centAmount) : null,
    price: cent(item?.originalPrice?.centAmount),
    currentPrice: isDiscountApplied ? cent(item?.price?.centAmount) : cent(item?.originalPrice?.centAmount),
    qty: item?.count ?? 1,
    total: cent(item?.totalPrice?.centAmount),
    custom: item?.custom,
    isMediaClipProduct: isMediaClip,
    reorder: {
      quantity: item?.reorder?.quantity ?? 0,
      distributionChannel: item?.reorder?.distributionChannel,
      productSku: item?.reorder?.productSku,
    },
    name: item?.name,
    _url: item?._url,
    lineItemId: item?.lineItemId,
    isSale: isDiscountApplied,
  };
};

type FindMediaClipItemParams = {
  cartData: Cart[];
  mediaClipProjectId?: string;
};

type MediaClipCartMatch = {
  cartId: string;
  lineItem: LineItem;
};

const findMediaClipItemInCarts = ({
  cartData,
  mediaClipProjectId,
}: FindMediaClipItemParams): MediaClipCartMatch | null => {
  if (!mediaClipProjectId) return null;

  for (const cart of cartData) {
    const lineItem = cart.lineItems?.find(
      (li) =>
        li?.custom?.fields?.mediaClipProjectId === mediaClipProjectId,
    );

    if (lineItem) {
      return {
        cartId: cart?.cartId,
        lineItem,
      };
    }
  }

  return null;
};

const OrderItemsSection = ({
  title,
  itemCount,
  mode,
  items,
  price,
  total,
  quantity,
  details,
  isType,
  channelName,
  shouldDisplayReorderButton,
  reorderText,
  index,
  shippingAddress,
  shippingInfo,
  allLineItems,
  detailsCTAmediaClip,
  extraPages,
  reasonCodes,
}: OrderItemsSectionProps) => {
  const { projectSettings } = useProjectSettings();
  const isPickup = mode === 'p2rCart';
  const isShip = mode === 'm2hCart';

  const cookies = useCookies();
  const locale = cookies.get('locale') as string;

  const { setShipping, miniCarts: cartData } = useCart();

  const extensions = sdk.composableCommerce;

  const { setModalOpen } = useAddToCartOverlay();

  const { showLoader, hideLoader } = usePageLoader();

  const { refreshImagesForOrders } = useRefreshImages();

  const [refreshedItems, setRefreshedItems] = useState<LineItem[]>(items);
  const safeItems: PickupItem[] = useMemo(
    () => refreshedItems.map((item: LineItem) => normalizeItem(item, locale)),
    [refreshedItems, locale],
  );
  const refreshedLineItemsById = useMemo(() => {
    return refreshedItems.reduce<Record<string, LineItem>>((acc, lineItem) => {
      if (lineItem.lineItemId) {
        acc[lineItem.lineItemId] = lineItem;
      }

      return acc;
    }, {});
  }, [refreshedItems]);

  const handleReorder = async (item: PickupItem) => {
    const child = allLineItems?.find((childItem) => childItem?.custom?.fields?.parentLineItem == item?.lineItemId);
    const parentProjectId = item?.custom?.fields?.mediaClipProjectId;
    const parentImageMetadataIds = getImageMetadataIdsFromCustomFields(item);
    const childImageMetadataIds = getImageMetadataIdsFromCustomFields(child ?? {});
    const existingCartMatch = findMediaClipItemInCarts({
      cartData: cartData,
      mediaClipProjectId: parentProjectId,
    });

    const existingParentLineItem = existingCartMatch?.lineItem;
    const existingCartId = existingCartMatch?.cartId;

    const parentCartLineItemQTY = existingParentLineItem
      ? (existingParentLineItem.count ?? 0) + 1
      : (item.reorder?.quantity ?? 1);

    const baseCartItem = {
      quantity: parentCartLineItemQTY,
      productId: item.reorder?.productSku,
      custom: {
        fields: {
          mediaClipProjectId: parentProjectId,
        },
      },
    };

    const payload = {
      cartItems: [baseCartItem],
      distributionChannelKey: item.reorder?.distributionChannel?.key ?? '',
      projectThumbnailUrl: item.image,
    };

    showLoader();

    try {
      let res: any;
      let createdParentLineItem: LineItem | undefined;

      if (existingParentLineItem) {
        const updateItemPayload = {
          cartId: existingCartId,
          lineItem: {
            id: existingParentLineItem?.lineItemId as string,
            count: parentCartLineItemQTY,
          },
          shouldCalculateTaxes: true,
        };
        res = await extensions.cart.updateItem(updateItemPayload);

      } else {
        res = await extensions.cart.updateLineItems(payload);
        createdParentLineItem = res?.data?.lineItems?.find(
          (lineItem: LineItem) =>
            lineItem?.custom?.fields?.mediaClipProjectId === parentProjectId,
        );
      }

      if (child && createdParentLineItem) {

        const extraPagePayload = {
          ...payload,
          cartItems: [
            {
              quantity: parentCartLineItemQTY,
              productId: item.reorder?.productSku,
              lineItemId: createdParentLineItem?.lineItemId,
              custom: {
                fields: {
                  mediaClipProjectId: parentProjectId,
                },
              },
            },
            {
              quantity: child?.count ?? 2,
              productId: child?.variant?.sku,
              custom: {
                fields: {
                  mediaClipProjectId: child?.custom?.fields?.mediaClipProjectId,
                  parentLineItem: createdParentLineItem?.lineItemId,
                },
              },
            },
          ],
        };
        const res2: any = await extensions.cart.updateLineItems(extraPagePayload);
        res = res2;
      }

      if (res?.data?.cartId && res?.data?.lineItems?.length) {
        const parentLineItem = res.data.lineItems.find(
          (lineItem: LineItem) => lineItem?.custom?.fields?.mediaClipProjectId == parentProjectId,
        );
        const childLineItem = parentLineItem
          ? res.data.lineItems.find(
            (lineItem: LineItem) =>
              lineItem?.custom?.fields?.parentLineItem == parentLineItem.lineItemId &&
              lineItem?.custom?.fields?.mediaClipProjectId == child?.custom?.fields?.mediaClipProjectId,
          )
          : undefined;

        const metadataUpdates = [
          {
            lineItemId: parentLineItem?.lineItemId,
            imageMetadataIds: parentImageMetadataIds,
          },
          {
            lineItemId: childLineItem?.lineItemId,
            imageMetadataIds: childImageMetadataIds,
          },
        ].filter((entry): entry is { lineItemId: string; imageMetadataIds: string[] } => {
          return Boolean(entry.lineItemId && entry.imageMetadataIds.length);
        });

        if (metadataUpdates.length) {
          await Promise.all(
            metadataUpdates.map((entry) =>
              extensions.cart.updateImageMetadataIds({
                cartId: res.data.cartId,
                lineItemId: entry.lineItemId,
                isAddTocart: true,
                imageMetadataIds: entry.imageMetadataIds,
              }),
            ),
          );
        }
      }

      mutate(miniCartsKey);
      if (res?.data?.cartId) {
        if (isPickup) {
          const channelValue = P2R_SHIPPING_METHOD_KEY[shippingInfo?.fulfillerId as string]?.split('-')[1];
          const selectedChanelValue = Array.from(P2R_VALUES).find((item) => item.includes(channelValue));
          const newShippingAddress = {
            shippingAddress: {
              streetName: shippingAddress?.streetName,
              additionalStreetInfo: shippingAddress?.additionalStreetInfo,
              postalCode: shippingAddress?.postalCode,
              city: shippingAddress?.city,
              region: shippingAddress?.region,
              country: shippingAddress?.country,
              fulfillerId: shippingInfo?.fulfillerId as string,
              storeNumber: shippingInfo?.storeInfo,
              fulfillmentType: 'p2r',
              fulfillmentCode: shippingInfo?.fulfillmentCode as string,
            },
            cartId: res?.data?.cartId,
            shippingMethod: {
              key: P2R_SHIPPING_METHOD_KEY[shippingInfo?.fulfillerId as string],
            },
            distributionChannel: {
              key: selectedChanelValue,
            },
          };

          await setShipping(newShippingAddress, projectSettings?.taxMode).then(() => {
            setModalOpen(true);
            toast.success(reorderText.reorderSuccessfulToast.replace('{{productName}}', item.subTitle || item.title));
          });
        } else {
          setModalOpen(true);
          toast.success(reorderText.reorderSuccessfulToast.replace('{{productName}}', item.subTitle || item.title));
        }
      } else {
        toast.error(reorderText.reorderFailedToast);
      }
    } catch (error) {
      console.error('Reorder failed', error);

      toast.error(reorderText.reorderFailedToast);
    } finally {
      hideLoader();
    }
  };

  const channelNameCaps = channelName?.toUpperCase() == 'CVS' ? channelName?.toUpperCase() : channelName;

  const reasonMessagesPerLineItem =
    items
      ?.map((li) => {
        const reasonCode = (li.custom as { fields?: { reasonCode?: string } })?.fields?.reasonCode;

        return reasonCodes?.find((item: ReasonCode) => item.reasonCode === reasonCode);
      })
      .filter((item): item is ReasonCode => Boolean(item)) ?? [];

  useEffect(() => {
    if (!items?.length) return;

    let cancelled = false;

    const refreshOrderDetailImages = async () => {
      try {
        const { refreshedLineItems } = await refreshImagesForOrders(items);

        if (cancelled) return;

        setRefreshedItems(refreshedLineItems);
      } catch (error) {
        console.error('Error refreshing order detail images', error);
      }
    };

    void refreshOrderDetailImages();

    return () => {
      cancelled = true;
    };
  }, [items, refreshImagesForOrders]);

  return (
    <div
      data-ffid={`ctr-order-items-${index}`}
      className={clsx(
        'border-[#EBEBEB] bg-white', // overflow fix
        isType === 'modalType' ? 'mb-12 rounded-lg p-12' : 'mb-12 rounded-xl p-12 sm:p-16',
      )}
    >
      <div className="mb-16 border-b border-[#EBEBEB] pb-8" data-ffid={`ctr-order-items-header-${index}`}>
        <div className="flex items-center justify-between" data-ffid={`ctr-order-items-header-row-${index}`}>
          <span
            data-ffid={`lbl-order-items-title-${index}`}
            className={clsx(
              isType === 'modalType'
                ? 'text-12 font-bold text-[#333333]'
                : 'text-14 font-semibold text-gray-800 lg:text-16',
            )}
          >
            {`${title} ${isPickup ? channelNameCaps : ''} (${itemCount})`}
          </span>

          <div
            className="flex items-center gap-4 text-14 font-semibold text-[#999999] sm:gap-8 lg:gap-12 lg:text-16"
            data-ffid={`ctr-order-items-headings-${index}`}
          >
            <span className="w-70 text-right sm:w-80" data-ffid={`lbl-order-items-price-${index}`}>
              {price}
            </span>
            <span className="w-30 text-center sm:w-38" data-ffid={`lbl-order-items-qty-${index}`}>
              {quantity}
            </span>
            <span className="w-60 text-right sm:w-72" data-ffid={`lbl-order-items-total-${index}`}>
              {total}
            </span>
          </div>
        </div>
      </div>

      {/* SHIP */}
      {isShip && (
        <div className="space-y-16" data-ffid={`ctr-order-items-ship-list-${index}`}>
          {safeItems.map((item, idx) => {
            const childLineItems = getChildLineItems(allLineItems, item?.lineItemId);
            const safeItemsChildren: PickupItem[] = childLineItems.map((item: LineItem) => normalizeItem(item, locale));
            const isChild = childLineItems && childLineItems?.length > 0;
            const mergedLineItems = mergeLineItems(
              allLineItems?.find((line) => line?.lineItemId == item.lineItemId) || {},
              childLineItems,
            );
            const shipPrices =
              isChild && mergedLineItems
                ? getMergedLineItemsTotals(mergedLineItems, locale)
                : getFormattedTotals(
                  [allLineItems?.find((line) => line?.lineItemId == item.lineItemId) || {}, ...childLineItems],
                  locale,
                );

            return (
              <div
                key={item?.lineItemId || `ship-item-${index}-${idx}`}
                className="flex flex-nowrap gap-6 border-b border-[#EBEBEB] pb-16 last:border-none sm:gap-16"
                data-ffid={`ctr-order-items-shiplist-item-${index}-${idx}`}
              >
                <OrderItemsThumbnailImage
                  item={item}
                  idx={`${index}-${idx}`}
                  lineItem={refreshedLineItemsById[item.lineItemId]}
                />
                <div className="w-full">
                  <div className="min-w-0 flex-1" data-ffid={`ctr-order-items-shiplist-info-${index}-${idx}`}>
                    <p
                      className="text-14 font-normal text-gray-800 lg:text-16"
                      data-ffid={`lbl-order-items-shiplist-title-${index}-${idx}`}
                    >
                      {item.title}
                    </p>
                  </div>

                  <div className="flex justify-between">
                    <p
                      className="text-14 text-[#666666] lg:text-16"
                      data-ffid={`lbl-order-items-shiplist-subtitle-${index}-${idx}`}
                    >
                      {item.subTitle}
                    </p>

                    {isChild && (
                      <OrderDetailsPriceRow
                        idx={idx}
                        index={index}
                        isChild={isChild}
                        item={item}
                        shipPrices={shipPrices}
                        ffid="ship"
                      />
                    )}
                  </div>

                  {item?.isMediaClipProduct && !isChild ? (
                    <></>
                  ) : (
                    <p
                      className={clsx(
                        'mb-8 cursor-pointer text-14 text-[#666666] lg:text-16',
                        item?.isMediaClipProduct && isChild ? 'mt-10 border-t-2 border-[#EBEBEB] pt-10' : 'underline',
                      )}
                      data-ffid={`lbl-order-items-shiplist-details-${index}-${idx}`}
                    >
                      {item?.isMediaClipProduct && isChild ? detailsCTAmediaClip : details}
                    </p>
                  )}

                  {!isChild && (
                    <div className="flex justify-between">
                      <div className="flex flex-col items-start gap-y-2 md:flex-row md:items-center md:gap-x-4">
                        <p
                          className="text-14 text-[#666666] lg:text-16"
                          data-ffid={`lbl-order-items-shiplist-attributes-${index}-${idx}`}
                        >
                          {item.attributes}
                        </p>
                        <ColoredTag isSale={item.isSale} inline />
                      </div>

                      {
                        <OrderDetailsPriceRow
                          idx={idx}
                          index={index}
                          isChild={isChild}
                          item={item}
                          shipPrices={shipPrices}
                          ffid="ship"
                        />
                      }
                    </div>
                  )}

                  {item?.isMediaClipProduct &&
                    isChild &&
                    [item, ...safeItemsChildren].map((child, childIndex) => {
                      return (
                        <div className="mb-8 flex justify-between" key={child?.lineItemId || childIndex}>
                          <div
                            className="text-14 text-[#666666] lg:text-16"
                            data-ffid={`lbl-order-items-shiplist-subtitle-${index}-${idx}`}
                          >
                            <div className="flex items-center gap-4">
                              <div className="size-5 rounded-full bg-black"></div>
                              <div data-ffid={`lbl-order-items-shiplist-extrapages-${index}-${idx}`}>
                                {childIndex == 0 ? child.attributes : extraPages}
                              </div>
                            </div>
                          </div>

                          {isChild && (
                            <div className="flex gap-4 sm:gap-8 lg:gap-12">
                              <div
                                className="flex min-w-70 max-w-140 flex-col-reverse items-end justify-end gap-x-8 gap-y-4 whitespace-nowrap text-right sm:min-w-80 lg:flex-row"
                                data-ffid={`ctr-order-items-shiplist-pricewrap-${index}-${idx}`}
                              >
                                {child.oldPrice && (
                                  <span
                                    className="text-12 text-[#5C5C5C] line-through lg:text-16"
                                    data-ffid={`lbl-order-items-shiplist-oldprice-${index}-${idx}`}
                                  >
                                    {child.price}
                                  </span>
                                )}
                                <span
                                  className="text-12 lg:text-16"
                                  data-ffid={`lbl-order-items-shiplist-price-${index}-${idx}`}
                                >
                                  {child.currentPrice}
                                </span>
                              </div>

                              <div
                                className="min-w-30 max-w-50 text-center text-12 sm:min-w-38 lg:text-16"
                                data-ffid={`lbl-order-items-shiplist-qty-${index}-${idx}`}
                              >
                                {child?.qty}
                              </div>

                              <div
                                className="min-w-60 max-w-100 text-right text-12 font-semibold sm:min-w-72 lg:text-16"
                                data-ffid={`lbl-order-items-shiplist-total-${index}-${idx}`}
                              >
                                {child.total}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}

                  {reasonMessagesPerLineItem && reasonMessagesPerLineItem?.length > 0 && (
                    <div
                      className="flex w-fit items-center gap-12 rounded-md border border-[#FBC02D] bg-[#FFFBEC] px-12 py-5"
                      data-ffid={`order-details-on-hold-info-${item?.name}`}
                      data-testid="order-details-on-hold-info"
                      key={`order-details-on-hold-info-${item?.name}`}
                    >
                      <div
                        className="flex size-20 shrink-0 items-center justify-center rounded-full text-20 font-bold italic shadow-sm"
                        data-ffid={`order-details-on-hold-info-icon-${item?.name}`}
                        data-testid="order-details-on-hold-info-icon"
                      >
                        <InformationCircleIcon color="#FBC02D" fontSize={20} />
                      </div>
                      <span
                        className="text-xs text-[#333333]"
                        data-ffid={`order-details-rm-${index}-${cleanString(reasonMessagesPerLineItem[0]?.codeDescription ?? '')}`}
                      >
                        {reasonMessagesPerLineItem[0]?.codeDescription ?? ''}
                      </span>
                    </div>
                  )}

                  <div className="flex justify-between">
                    {shouldDisplayReorderButton && item.isMediaClipProduct && (
                      <button
                        type="button"
                        className="mt-8 text-14 text-blue-200 hover:underline lg:text-16"
                        data-ffid={`lbl-order-items-reorder-${idx}`}
                        onClick={() => handleReorder(item)}
                      >
                        {reorderText?.reorderBtnText}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* PICKUP */}
      {isPickup && (
        <div className="space-y-16" data-ffid="ctr-order-items-pickup">
          {safeItems.map((item, idx) => {
            const childLineItems = getChildLineItems(allLineItems, item?.lineItemId);
            const safeItemsChildren: PickupItem[] = childLineItems.map((item: LineItem) => normalizeItem(item, locale));

            const isChild = childLineItems && childLineItems.length > 0;
            const mergedLineItems = mergeLineItems(
              allLineItems?.find((line) => line?.lineItemId == item.lineItemId) || {},
              childLineItems,
            );
            const shipPrices =
              isChild && mergedLineItems
                ? getMergedLineItemsTotals(mergedLineItems, locale)
                : getFormattedTotals(
                  [allLineItems?.find((line) => line?.lineItemId == item.lineItemId) || {}, ...childLineItems],
                  locale,
                );
            const mediaClipSpace = item.isMediaClipProduct && isChild && <div className="pb-10">&nbsp;</div>;
            return (
              <div
                key={item.lineItemId || `pickup-item-${index}-${idx}`}
                className="flex flex-nowrap gap-6 border-b border-[#EBEBEB] pb-16 last:border-none sm:gap-16"
                data-ffid={`ctr-order-items-pickup-item-${index}-${idx}`}
              >
                <OrderItemsThumbnailImage
                  item={item}
                  idx={`${index}-${idx}`}
                  lineItem={refreshedLineItemsById[item.lineItemId]}
                />

                <div className="min-w-0 flex-1" data-ffid={`ctr-order-items-pickup-info-${index}-${idx}`}>
                  <p
                    className="text-14 font-normal text-gray-800 lg:text-16"
                    data-ffid={`lbl-order-items-pickup-title-${index}-${idx}`}
                  >
                    {item.title}
                  </p>

                  <p
                    className="text-14 text-[#333333] lg:text-16"
                    data-ffid={`lbl-order-items-pickup-subtitle-${index}-${idx}`}
                  >
                    {item.subTitle}
                  </p>
                  {item?.isMediaClipProduct && (
                    <MediaClipElement
                      isChild={isChild}
                      item={item}
                      childLineItems={childLineItems}
                      idx={idx}
                      index={index}
                      text={{ extraPages, detailsCTAmediaClip }}
                    />
                  )}

                  {!item?.isMediaClipProduct && item?.attributes && (
                    <div className="flex flex-col items-start gap-y-2 md:flex-row md:items-center md:gap-x-4">
                      <p
                        className="text-14 text-[#333333] lg:text-16"
                        data-ffid={`lbl-order-items-pickup-attributes-${index}-${idx}`}
                      >
                        {item?.attributes}
                      </p>
                      <ColoredTag isSale={item.isSale} inline />
                    </div>
                  )}
                  {reasonMessagesPerLineItem && reasonMessagesPerLineItem?.length > 0 && (
                    <div
                      className="flex w-fit items-center gap-12 rounded-md border border-[#FBC02D] bg-[#FFFBEC] px-12 py-5"
                      data-ffid={`order-details-on-hold-info-${item?.name}`}
                      data-testid="order-details-on-hold-info"
                      key={`order-details-on-hold-info-${item?.name}`}
                    >
                      <div
                        className="flex size-20 shrink-0 items-center justify-center rounded-full text-20 font-bold italic shadow-sm"
                        data-ffid={`order-details-on-hold-info-icon-${item?.name}`}
                        data-testid="order-details-on-hold-info-icon"
                      >
                        <InformationCircleIcon color="#FBC02D" fontSize={20} />
                      </div>
                      <span
                        className="text-xs text-[#333333]"
                        data-ffid={`order-details-rm-${index}-${cleanString(reasonMessagesPerLineItem[0]?.codeDescription ?? '')}`}
                      >
                        {reasonMessagesPerLineItem[0]?.codeDescription ?? ''}
                      </span>
                    </div>
                  )}
                  {shouldDisplayReorderButton && item?.isMediaClipProduct && (
                    <button
                      type="button"
                      className="mt-8 text-14 text-blue-200 hover:underline lg:text-16"
                      data-ffid={`lbl-order-items-reorder-${idx}`}
                      onClick={() => handleReorder(item)}
                    >
                      {reorderText?.reorderBtnText}
                    </button>
                  )}
                </div>

                <div className="ml-auto flex flex-nowrap items-center gap-4 sm:gap-8 lg:gap-12">
                  <div>
                    <div
                      className="flex min-w-70 max-w-140 flex-col items-end justify-end gap-8 whitespace-nowrap text-right sm:min-w-80 lg:flex-row"
                      data-ffid={`ctr-order-items-pickup-pricewrap-${index}-${idx}`}
                    >
                      {item.oldPrice && (
                        <span
                          className="text-12 text-[#5C5C5C] line-through lg:text-16"
                          data-ffid={`lbl-order-items-pickup-oldprice-${index}-${idx}`}
                        >
                          {item.isMediaClipProduct && isChild ? shipPrices?.price : item.price}
                        </span>
                      )}
                      <span className="text-12 lg:text-16" data-ffid={`lbl-order-items-pickup-price-${index}-${idx}`}>
                        {item.isMediaClipProduct && isChild ? shipPrices?.currentPrice : item.currentPrice}
                      </span>
                    </div>
                    {mediaClipSpace}
                    {item.isMediaClipProduct &&
                      isChild &&
                      [item, ...safeItemsChildren].map((child, i) => {
                        return (
                          <div
                            key={child?.lineItemId || i}
                            className="flex min-w-70 max-w-140 flex-col items-end justify-end gap-8 whitespace-nowrap text-right sm:min-w-80 lg:flex-row"
                            data-ffid={`ctr-order-items-pickup-pricewrap-${index}-${idx}-${i}`}
                          >
                            {child?.oldPrice && (
                              <span
                                className="text-12 text-[#5C5C5C] line-through lg:text-16"
                                data-ffid={`lbl-order-items-pickup-oldprice-${index}-${idx}-${i}`}
                              >
                                {child?.price}
                              </span>
                            )}
                            <span
                              className="text-12 lg:text-16"
                              data-ffid={`lbl-order-items-pickup-price-${index}-${idx}-${i}`}
                            >
                              {child?.currentPrice}
                            </span>
                          </div>
                        );
                      })}
                  </div>

                  <div>
                    <div
                      className="min-w-30 max-w-50 text-center text-12 sm:min-w-38 lg:text-16"
                      data-ffid={`lbl-order-items-pickup-qty-${index}-${idx}`}
                    >
                      {item.qty}
                    </div>
                    {mediaClipSpace}
                    {item.isMediaClipProduct &&
                      isChild &&
                      [item, ...safeItemsChildren].map((child, i) => {
                        return (
                          <div
                            key={'child' + i}
                            className="min-w-30 max-w-50 text-center text-12 sm:min-w-38 lg:text-16"
                            data-ffid={`lbl-order-items-pickup-qty-${index}-${idx}-${i}`}
                          >
                            {child?.qty}
                          </div>
                        );
                      })}
                  </div>

                  <div>
                    <div
                      className="min-w-60 max-w-100 text-right text-12 font-semibold sm:min-w-72 lg:text-16"
                      data-ffid={`lbl-order-items-pickup-total-${index}-${idx}`}
                    >
                      {item.isMediaClipProduct && isChild ? shipPrices?.total : item.total}
                    </div>
                    {mediaClipSpace}
                    {item?.isMediaClipProduct &&
                      isChild &&
                      [item, ...safeItemsChildren].map((child, i) => {
                        return (
                          <div
                            key={'child' + i}
                            className="min-w-60 max-w-100 text-right text-12 font-semibold sm:min-w-72 lg:text-16"
                            data-ffid={`lbl-order-items-pickup-total-${index}-${idx}-${i}`}
                          >
                            {child?.total}
                          </div>
                        );
                      })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OrderItemsSection;
